import { Link } from "react-router-dom";

import Footer from "@/components/Footer";
import SharedHeader from "@/components/SharedHeader";
import { WHATSAPP_REGION_AVAILABILITY } from "@/lib/messaging";
import { theme } from "@/lib/theme";
import { cn } from "@/lib/utils";

const linkClass = cn("font-semibold hover:underline", theme.text.primaryOrange);

export default function PrivacyPage() {
  return (
    <div className={cn("min-h-screen flex flex-col", theme.colors.backgroundCream)}>
      <SharedHeader />

      <main className="mx-auto max-w-3xl flex-1 px-6 py-16 pt-24">
        <article className="space-y-8">
          <header className="mb-12 text-center">
            <h1 className="mb-4 text-4xl font-black text-gray-900 dark:text-white md:text-5xl">
              Privacy Policy
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">Last updated: July 19, 2026</p>
          </header>

          <section className={cn(theme.cards.base, "space-y-4 p-6")}>
            <p className="leading-relaxed text-gray-700 dark:text-gray-300">
              This policy explains what Podwise GPT collects, why we use it, how long we keep it, and the choices available to you when you use our website, apps, API, MCP tools, and messaging features (the “Service”).
            </p>
          </section>

          <section className={cn(theme.cards.base, "space-y-4 p-6")}>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">1. Information we collect</h2>
            <ul className="list-disc space-y-3 pl-5 leading-relaxed text-gray-700 dark:text-gray-300">
              <li>
                <strong>Account information:</strong> your email address, optional display and language settings, authentication records, subscription status, and account timestamps.
              </li>
              <li>
                <strong>Podcast information:</strong> shows you follow, notification preferences, feed URLs and credentials you choose to provide, episode metadata, transcripts, and indexes needed to search podcast content.
              </li>
              <li>
                <strong>Questions and sessions:</strong> questions, AI-generated answers, conversation history, source evidence, timestamps, and session state created through the web app, API, or MCP tools. Some features let you delete individual sessions.
              </li>
              <li>
                <strong>Usage and billing records:</strong> token and feature usage, credit balances, cost calculations, plan identifiers, invoices, payment status, and Stripe customer or subscription identifiers. Stripe processes full payment-card details; Podwise does not store your full card number.
              </li>
              <li>
                <strong>MCP credentials:</strong> a one-way hash of your MCP token plus creation and coarse last-used timestamps. The raw token is shown only when created or rotated and is not stored by Podwise.
              </li>
              <li>
                <strong>Signal information:</strong> an opaque account-link and routing identifier derived from your Signal identity, consent and opt-out records, message content, delivery state, conversation context, provider timestamps, and research-usage records when you connect through Signal. Podwise does not ask you to enter your Signal number on the web setup page.
              </li>
              <li>
                <strong>Telegram information:</strong> an opaque account-link and routing identifier derived from your Telegram user ID, consent and opt-out records, message content, delivery state, conversation context, Telegram message identifiers, and research-usage records when you connect through Telegram.
              </li>
              <li>
                <strong>WhatsApp information:</strong> account-link and routing information supplied through WhatsApp and Meta, registration country and identity type when available, consent and opt-out records, message content, delivery state, conversation context, provider message identifiers, and transport cost records when you connect a WhatsApp account.
              </li>
              <li>
                <strong>Operational information:</strong> IP address, user agent, request and security logs, error diagnostics, rate-limit events, and similar records needed to secure and operate the Service.
              </li>
            </ul>
          </section>

          <section className={cn(theme.cards.base, "space-y-4 p-6")}>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">2. How we use information</h2>
            <ul className="list-disc space-y-2 pl-5 leading-relaxed text-gray-700 dark:text-gray-300">
              <li>Authenticate your account and maintain your settings.</li>
              <li>Ingest, transcribe, index, search, and answer questions about podcasts.</li>
              <li>Maintain conversation context and deliver requested answers across supported interfaces.</li>
              <li>Process subscriptions, credits, usage charges, receipts, and billing support.</li>
              <li>Send transactional email, podcast notifications, and consented Signal, Telegram, or WhatsApp service messages.</li>
              <li>Prevent abuse, enforce rate limits, debug failures, and improve reliability.</li>
              <li>Respond to support requests and comply with legal obligations.</li>
            </ul>
            <p className="leading-relaxed text-gray-700 dark:text-gray-300">
              We do not sell your personal information or use messaging content for advertising.
              Signal, Telegram, and WhatsApp questions and answers are sent to AI providers only under data controls that prohibit
              using that content to train or improve shared models. Podwise also requests zero-data-retention
              routing where the selected provider and Podwise account support it; a provider may still keep
              limited security or abuse-monitoring records under its applicable policy.
            </p>
          </section>

          <section className={cn(theme.cards.base, "space-y-4 p-6")}>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">3. Service providers</h2>
            <p className="leading-relaxed text-gray-700 dark:text-gray-300">
              We disclose only the information needed for providers to perform services for Podwise. Depending on the feature you use, these providers include:
            </p>
            <ul className="list-disc space-y-3 pl-5 leading-relaxed text-gray-700 dark:text-gray-300">
              <li>
                <strong>Stripe</strong> for billing and payment processing. See{" "}
                <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className={linkClass}>
                  Stripe’s privacy policy
                </a>.
              </li>
              <li>
                <strong>Signal</strong> to transport Signal messages between your devices and the Podwise linked device. Signal processes the routing and encrypted delivery data needed to provide its service under its own privacy practices.
              </li>
              <li>
                <strong>Telegram</strong> to transport Telegram bot messages. Telegram receives the account-routing information, message content, and delivery metadata needed to provide its service and applies its own privacy and retention practices.
              </li>
              <li>
                <strong>Twilio and Meta</strong> to route and deliver WhatsApp messages. These companies receive the WhatsApp account-routing information, message content, and delivery metadata needed to provide the feature and apply their own retention practices. See{" "}
                <a href="https://www.twilio.com/en-us/legal/privacy" target="_blank" rel="noopener noreferrer" className={linkClass}>
                  Twilio’s privacy notice
                </a>{" "}
                and{" "}
                <a href="https://www.facebook.com/privacy/policy/" target="_blank" rel="noopener noreferrer" className={linkClass}>
                  Meta’s privacy policy
                </a>.
              </li>
              <li>
                <strong>AI, transcription, hosting, email, database, and network providers</strong> to process requested content and operate the Service. This includes Cloudflare for network and messaging ingress services.
              </li>
            </ul>
            <p className="leading-relaxed text-gray-700 dark:text-gray-300">
              Information may also be disclosed when required by law, to protect users or the Service, or as part of a business reorganization subject to appropriate safeguards.
            </p>
          </section>

          <section className={cn(theme.cards.base, "space-y-4 p-6")}>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">4. Retention and deletion</h2>
            <ul className="list-disc space-y-3 pl-5 leading-relaxed text-gray-700 dark:text-gray-300">
              <li>
                Account information, followed-podcast settings, and active credential metadata are generally retained while your account exists or as needed to provide the feature.
              </li>
              <li>
                Web, API, and MCP question sessions, evidence, usage history, and operational traces are retained according to the lifecycle of the relevant feature and for reliability, security, billing, and dispute handling. Deleting a visible session removes it from normal account access but may not immediately remove backups or required audit records.
              </li>
              <li>
                For Signal, Podwise schedules message contents and protected routing data for redaction after <strong>7 days</strong>. Body-free event rows may remain under the audit schedule below. Encrypted follow-up context is retained only for the active research scope and expires after the configured idle window.
              </li>
              <li>
                For Telegram, Podwise schedules message contents and protected routing data for redaction after <strong>7 days</strong>. Body-free event rows may remain under the audit schedule below. Encrypted follow-up context is retained only for the active research scope and expires after the configured idle window.
              </li>
              <li>
                For WhatsApp, Podwise schedules message contents and protected routing data for redaction after <strong>7 days</strong>. Body-free event rows may remain under the audit schedule below. To support follow-up questions, encrypted conversation memory may retain up to three recent question-and-answer excerpts, together with evidence references, for <strong>30 days</strong>.
              </li>
              <li>
                Signal and Telegram consent, opt-out, delivery, usage, and security audit records that do not contain message bodies are generally retained for <strong>1 year</strong>, unless a longer period is required for billing, fraud prevention, or law.
              </li>
              <li>
                WhatsApp consent, opt-out, delivery, cost, and security audit records that do not contain message bodies are generally retained for <strong>1 year</strong>, unless a longer period is required for billing, fraud prevention, or law.
              </li>
              <li>
                Your active Signal account-link relationship and its protected routing material are retained while the account remains linked or opted out. Unlinking removes active routing material; the body-free identity and consent record may remain for up to <strong>1 year after unlinking</strong>. Sending STOP opts you out but does not delete your Podwise account.
              </li>
              <li>
                Your active Telegram account-link relationship and its protected routing material are retained while the account remains linked or opted out. Unlinking removes active routing material; the body-free identity and consent record may remain for up to <strong>1 year after unlinking</strong>. Sending STOP opts you out but does not delete your Podwise account.
              </li>
              <li>
                Your active WhatsApp account-link relationship and its protected routing material are retained while the account remains linked or opted out. Unlinking removes the active routing material; the body-free identity and consent record may remain for up to <strong>1 year after unlinking</strong>. Sending STOP opts you out of messages but does not unlink the WhatsApp account or delete your Podwise account.
              </li>
              <li>
                The 7-day deletion schedule applies to Podwise&apos;s own message-body storage. Signal, Telegram, Twilio, Meta, Stripe, AI providers, and other processors may independently retain identifiers, content, backups, or legal records for longer under their own policies.
              </li>
            </ul>
            <p className="leading-relaxed text-gray-700 dark:text-gray-300">
              Canceling a paid subscription stops future renewal and may pause paid features; it does <strong>not</strong> automatically delete your account or all associated data. To request account deletion, contact us at the address below.
            </p>
          </section>

          <section className={cn(theme.cards.base, "space-y-4 p-6")}>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">5. Your choices</h2>
            <ul className="list-disc space-y-2 pl-5 leading-relaxed text-gray-700 dark:text-gray-300">
              <li>Change podcast and notification settings from your account.</li>
              <li>Delete supported chat sessions and revoke MCP access.</li>
              <li>Send STOP to opt out of Signal, Telegram, or WhatsApp, START to opt back in, or unlink the messaging account from Settings.</li>
              <li>Request access, correction, export, or deletion of personal information, subject to applicable legal exceptions.</li>
            </ul>
            <p className="leading-relaxed text-gray-700 dark:text-gray-300">
              Contact{" "}
              <a href="mailto:quiet.lens2873@fastmail.com" className={linkClass}>
                quiet.lens2873@fastmail.com
              </a>{" "}
              to exercise a privacy right. We may need to verify your identity before completing a request.
            </p>
          </section>

          <section className={cn(theme.cards.base, "space-y-4 p-6")}>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">6. Browser storage and security</h2>
            <p className="leading-relaxed text-gray-700 dark:text-gray-300">
              Podwise uses essential browser storage for authentication and preferences such as theme. We also receive ordinary network and security logs. We do not currently use third-party advertising cookies.
            </p>
            <p className="leading-relaxed text-gray-700 dark:text-gray-300">
              We use safeguards such as HTTPS, access controls, protected credentials and messaging account links, protection for messaging content, and rate limits. No system is completely secure. Keep login links and MCP tokens private, and remember that device notifications, clipboard contents, shell history, and client configuration can expose information outside Podwise’s control.
            </p>
          </section>

          <section className={cn(theme.cards.base, "space-y-4 p-6")}>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">7. International processing and children</h2>
            <p className="leading-relaxed text-gray-700 dark:text-gray-300">
              Podwise and its providers may process information in countries other than your own. Those locations may have different privacy laws. The Service is not directed to children under 13, and we do not knowingly collect personal information from children under 13.
            </p>
            <p className="leading-relaxed text-gray-700 dark:text-gray-300">
              {WHATSAPP_REGION_AVAILABILITY}
            </p>
          </section>

          <section className={cn(theme.cards.base, "space-y-4 p-6")}>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">8. Changes and contact</h2>
            <p className="leading-relaxed text-gray-700 dark:text-gray-300">
              We may update this policy as the Service changes. We will post the updated version here, change the date above, and provide additional notice when required.
            </p>
            <p className="leading-relaxed text-gray-700 dark:text-gray-300">
              Questions or requests can be sent to{" "}
              <a href="mailto:quiet.lens2873@fastmail.com" className={linkClass}>
                quiet.lens2873@fastmail.com
              </a>.
            </p>
          </section>

          <section className={cn(theme.cards.base, "space-y-4 p-6")}>
            <p className="leading-relaxed text-gray-700 dark:text-gray-300">
              For account, acceptable-use, and billing rules, read our{" "}
              <Link to="/terms" className={linkClass}>Terms of Service</Link>.
            </p>
          </section>
        </article>
      </main>

      <Footer />
    </div>
  );
}
